import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Skeleton,
  Divider,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Search as SearchIcon,
  Inventory2 as Inventory2Icon,
  Category as CategoryIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { getProductsReportData } from '../../../services/ReportsProductsService';
import PageHeader from '../../../components/PageHeader/PageHeader';
import SummaryCards from '../../../components/Reports/SummaryCards';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
dayjs.extend(isoWeek);

const PRESETS = [
  { value: 'today', label: 'Hoy' },
  { value: 'week', label: 'Esta semana' },
  { value: 'month', label: 'Este mes' },
  { value: 'custom', label: 'Personalizado' },
];

const getPresetRange = (preset) => {
  const today = dayjs();
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'week':
      return { from: today.startOf('isoWeek'), to: today };
    case 'custom':
      return null;
    case 'month':
    default:
      return { from: today.startOf('month'), to: today };
  }
};

const formatNumber = (value = 0) => Number(value || 0).toLocaleString('de-DE');

const ProductsReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const [preset, setPreset] = useState('month');
  const [customFrom, setCustomFrom] = useState(dayjs().startOf('month'));
  const [customTo, setCustomTo] = useState(dayjs());
  const [branch, setBranch] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [groupBy, setGroupBy] = useState('product');
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('quantity');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleGroupByChange = (event, value) => {
    if (!value) return;
    setGroupBy(value);
    setOrderBy('quantity');
    setOrder('desc');
  };

  const { from, to } = useMemo(() => {
    const range = preset === 'custom' ? { from: customFrom, to: customTo } : getPresetRange(preset);
    return {
      from: range.from.format('YYYY-MM-DD'),
      to: range.to.format('YYYY-MM-DD'),
    };
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getProductsReportData({ from, to });
        if (active) {
          setRows(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching products report', error);
        if (active) {
          setRows([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [from, to, reportCurrency]);

  const branches = useMemo(
    () => Array.from(new Set(rows.map((row) => row.branch))).sort(),
    [rows]
  );

  const categories = useMemo(
    () => Array.from(new Set(rows.map((row) => row.category))).sort(),
    [rows]
  );

  const branchFilteredRows = useMemo(
    () => rows.filter((row) => branch === 'ALL' || row.branch === branch),
    [rows, branch]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (groupBy === 'category') {
      const totals = new Map();
      branchFilteredRows.forEach((row) => {
        const key = `${row.branch}|${row.category}`;
        const existing = totals.get(key) || { branch: row.branch, category: row.category, quantity: 0 };
        existing.quantity += row.quantity;
        totals.set(key, existing);
      });
      return Array.from(totals.values())
        .filter((row) => (row.category || '').toLowerCase().includes(term));
    }

    return branchFilteredRows
      .filter((row) => category === 'ALL' || row.category === category)
      .filter((row) => (row.product || '').toLowerCase().includes(term));
  }, [branchFilteredRows, groupBy, category, search]);

  const sortedRows = useMemo(() => {
    const sign = order === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (typeof valueA === 'string') return sign * valueA.localeCompare(valueB);
      return sign * (valueA - valueB);
    });
  }, [filteredRows, order, orderBy]);

  const summary = useMemo(() => {
    const totalQuantity = branchFilteredRows.reduce((acc, row) => acc + row.quantity, 0);
    const distinctProducts = new Set(branchFilteredRows.map((row) => row.product)).size;
    const distinctCategories = new Set(branchFilteredRows.map((row) => row.category)).size;
    return { totalQuantity, distinctProducts, distinctCategories };
  }, [branchFilteredRows]);

  const summaryItems = [
    {
      title: 'Unidades vendidas',
      icon: <Inventory2Icon color="primary" fontSize="large" />,
      value: formatNumber(summary.totalQuantity),
    },
    groupBy === 'category'
      ? {
          title: 'Categorías',
          icon: <CategoryIcon color="action" fontSize="large" />,
          value: summary.distinctCategories,
        }
      : {
          title: 'Productos distintos',
          icon: <CategoryIcon color="action" fontSize="large" />,
          value: summary.distinctProducts,
        },
  ];

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('desc');
    }
  };

  const columns = groupBy === 'category'
    ? [
        { id: 'branch', label: 'Sucursal', align: 'left' },
        { id: 'category', label: 'Categoría', align: 'left' },
        { id: 'quantity', label: 'Cantidad', align: 'right' },
      ]
    : [
        { id: 'branch', label: 'Sucursal', align: 'left' },
        { id: 'product', label: 'Producto', align: 'left' },
        { id: 'category', label: 'Categoría', align: 'left' },
        { id: 'quantity', label: 'Cantidad', align: 'right' },
      ];

  const handleExport = () => {
    const csvRows = [columns.map((column) => column.label)];
    sortedRows.forEach((row) => {
      csvRows.push(columns.map((column) => (column.id === 'quantity' ? row.quantity : row[column.id] ?? '')));
    });
    const csvContent = csvRows
      .map((cols) => cols.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `productos_${groupBy === 'category' ? 'por_categoria' : 'por_producto'}_${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box p={3}>
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title="Productos" subtitle="Detalle de unidades y montos vendidos por producto" />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
        <Grid item xs={12} md="auto">
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 260 } }}>
            <InputLabel id="branch-filter-label">Sucursal</InputLabel>
            <Select
              labelId="branch-filter-label"
              value={branch}
              label="Sucursal"
              onChange={(event) => setBranch(event.target.value)}
            >
              <MenuItem value="ALL">Todas las sucursales</MenuItem>
              {branches.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box mt={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item>
              <ToggleButtonGroup
                size="small"
                value={preset}
                exclusive
                onChange={(event, value) => value && setPreset(value)}
              >
                {PRESETS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            {preset === 'custom' && (
              <Grid item>
                <Box display="flex" gap={2}>
                  <DatePicker
                    label="Desde"
                    value={customFrom}
                    onChange={(value) => value && setCustomFrom(value)}
                    disableFuture
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label="Hasta"
                    value={customTo}
                    onChange={(value) => value && setCustomTo(value)}
                    disableFuture
                    minDate={customFrom}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <ToggleButtonGroup size="small" value={groupBy} exclusive onChange={handleGroupByChange}>
                <ToggleButton value="product">Por producto</ToggleButton>
                <ToggleButton value="category">Por categoría</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {groupBy === 'product' && (
              <Grid item>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="category-filter-label">Categoría</InputLabel>
                  <Select
                    labelId="category-filter-label"
                    value={category}
                    label="Categoría"
                    onChange={(event) => setCategory(event.target.value)}
                  >
                    <MenuItem value="ALL">Todas las categorías</MenuItem>
                    {categories.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs>
              <TextField
                fullWidth
                size="small"
                placeholder={groupBy === 'category' ? 'Buscar categoría...' : 'Buscar producto...'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      <Box mt={2}>
        <Paper elevation={2}>
          <Box display="flex" justifyContent="flex-end" p={1.5}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={loading || sortedRows.length === 0}
            >
              Exportar
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'desc'}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <TableRow key={index}>
                      {columns.map((column) => (
                        <TableCell key={column.id} align={column.align}>
                          <Skeleton width={`${40 + Math.random() * 40}%`} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortedRows.length > 0 ? (
                  sortedRows.map((row, index) => (
                    <TableRow key={`${row.branch}-${row.product || row.category}-${index}`} hover>
                      <TableCell>{row.branch}</TableCell>
                      {groupBy === 'product' && <TableCell>{row.product}</TableCell>}
                      <TableCell>{row.category}</TableCell>
                      <TableCell align="right">{formatNumber(row.quantity)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography variant="body2" color="textSecondary" py={3}>
                        No hay datos para el período seleccionado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default ProductsReports;
